import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { usePromptInputStore } from "../view_stores/usePromptInputStore";
import { useSubscriptionStore } from "./useSubscriptionStore";

// Model IDs - matches backend ModelSelectionSchema
export type ModelId =
  | "auto"
  | "claude-haiku-4-5"
  | "claude-sonnet-4-5"
  | "claude-opus-4-6";

export interface ModelInfo {
  id: ModelId;
  name: string;
  fullName: string;
  description: string;
  tokenUsageDescription: string;
  speed: number; // 0-10 scale (based on tokens/sec benchmarks)
  intelligence: number; // 0-10 scale (based on SWE-bench, GPQA)
  tokenUsage: number; // 0-10 scale (based on relative pricing)
  uiDesign: number; // 0-10 scale (UI/frontend design quality, Opus as baseline)
}

// Models ordered: Auto, Opus, Sonnet, Haiku (top to bottom in dropdown)
// Progress bar values based on official Anthropic benchmarks (0-10 scale)
// Sources: platform.claude.com, artificialanalysis.ai, vellum.ai, caylent.com
export const AVAILABLE_MODELS: ModelInfo[] = [
  {
    id: "auto",
    name: "Auto",
    fullName: "Auto",
    description: "Automatically selects the best Anthropic model for each task.",
    tokenUsageDescription: "Token usage varies based on task complexity.",
    speed: 7,
    intelligence: 9,
    tokenUsage: 6,
    uiDesign: 8,
  },
  {
    id: "claude-opus-4-6",
    name: "Opus 4.6",
    fullName: "Claude Opus 4.6",
    description: "Most capable model with best-in-class UI design and coding capabilities.",
    tokenUsageDescription: "Increased token usage compared to other models.",
    speed: 3,         // "Moderate" latency, slower for complex reasoning
    intelligence: 10, // 80.9% SWE-bench, 87.0% GPQA (best)
    tokenUsage: 10,   // $5/$25 per MTok (highest)
    uiDesign: 10,     // Baseline. Sophisticated design reasoning, iterative refinement (20+ passes)
  },
  {
    id: "claude-sonnet-4-5",
    name: "Sonnet 4.5",
    fullName: "Claude Sonnet 4.5",
    description: "Smart model for complex agents and coding. Best balance of intelligence, speed, and cost.",
    tokenUsageDescription: "Moderate token consumption. Ideal baseline for most production workloads.",
    speed: 6,         // 63 tokens/sec, 1.8s latency
    intelligence: 8,  // 77.2% SWE-bench, 83.4% GPQA
    tokenUsage: 5,    // $3/$15 per MTok (middle tier)
    uiDesign: 7,      // Solid production UI, but lacks sophisticated design tradeoff reasoning
  },
  {
    id: "claude-haiku-4-5",
    name: "Haiku 4.5",
    fullName: "Claude Haiku 4.5",
    description: "Speed-optimized model for simpler tasks.",
    tokenUsageDescription: "Significantly reduced token usage compared to other models.",
    speed: 10,        // 100+ chars/sec, 0.3s latency, 4-5x faster than Sonnet
    intelligence: 6,  // 73.3% SWE-bench, 73% GPQA
    tokenUsage: 2,    // $1/$5 per MTok (cheapest)
    uiDesign: 3,      // Speed tool, not design tool. Simple tweaks only, no design reasoning
  },
];

interface ModelState {
  selectedModel: ModelId;
  setSelectedModel: (model: ModelId) => void;
}

export const useModelStore = create<ModelState>()(
  persist(
    subscribeWithSelector((set) => ({
      selectedModel: "auto", // Default - automatically selects best model for each task

      setSelectedModel: (model: ModelId) => {
        set({ selectedModel: model });

        // Clear attached design if switching to a model that doesn't support Design Library
        // Only Auto and Opus models support Design Library
        const supportsDesignLibrary =
          model === "auto" || model.startsWith("claude-opus");
        if (!supportsDesignLibrary) {
          usePromptInputStore.getState().actions.setAttachedDesign(null);
        }
      },
    })),
    {
      name: "model-selection", // LocalStorage key
    }
  )
);

// Selector hooks
export const useSelectedModel = () =>
  useModelStore((state) => state.selectedModel);

export const useSelectedModelInfo = () =>
  useModelStore((state) => {
    const model = state.selectedModel;
    return AVAILABLE_MODELS.find((m) => m.id === model) || AVAILABLE_MODELS[0];
  });

export const useSetSelectedModel = () =>
  useModelStore((state) => state.setSelectedModel);

// Reset model to "auto" if user switches to a free plan with a locked model selected
// This handles the case where a paid user selects a premium model, logs out,
// and a free user logs in - the model should reset without requiring page refresh
useSubscriptionStore.subscribe((state) => {
  const subscription = state.subscription;
  const isFreePlan = !subscription || subscription.free;
  const currentModel = useModelStore.getState().selectedModel;

  if (isFreePlan && currentModel !== "auto") {
    useModelStore.getState().setSelectedModel("auto");
  }
});
